import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import './BlogPost.css';

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post,    setPost]    = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .single();
      setPost(data);

      if (data) {
        const { data: rel } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('published', true)
          .eq('category', data.category)
          .neq('id', data.id)
          .limit(3);
        setRelated(rel || []);
      }
      setLoading(false);
    };
    load();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return (
    <div className="bp-loading">
      <div className="bp-spinner" />
    </div>
  );

  if (!post) return (
    <div className="bp-notfound">
      <h2>Post not found.</h2>
      <button onClick={() => navigate('/blog')}>← Back to Blog</button>
    </div>
  );

  const formatDate = (d) => new Date(d).toLocaleDateString('en-ZA', {
    day: 'numeric', month: 'long', year: 'numeric'
  });

  const postUrl   = `https://www.sadropped.co.za/blog/${post.slug}`;
  const postImage = post.cover_image || 'https://www.sadropped.co.za/og-image.jpg';

  const renderBody = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return <h2 key={i} className="bp__body-h2">{line.slice(3)}</h2>;
      }
      if (line.startsWith('# ')) {
        return <h3 key={i} className="bp__body-h3">{line.slice(2)}</h3>;
      }
      if (line.trim() === '') {
        return <br key={i} />;
      }
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} className="bp__body-p">
          {parts.map((part, j) =>
            part.startsWith('**') && part.endsWith('**')
              ? <strong key={j}>{part.slice(2, -2)}</strong>
              : part
          )}
        </p>
      );
    });
  };

  return (
    <>
      <Helmet>
        {/* ── Core ── */}
        <title>{post.title} | SA Dropped Journal</title>
        <meta name="description" content={post.excerpt || `${post.title} — Read more on the SA Dropped Journal.`} />
        <meta name="keywords" content={`${post.category}, SA Dropped, sneaker culture South Africa, streetwear SA, ${post.title}`} />
        <meta name="author" content={post.author || 'SA Dropped'} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={postUrl} />

        {/* ── Open Graph — fully dynamic ── */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="SA Dropped" />
        <meta property="og:title" content={`${post.title} | SA Dropped Journal`} />
        <meta property="og:description" content={post.excerpt || post.title} />
        <meta property="og:url" content={postUrl} />
        <meta property="og:image" content={postImage} />
        <meta property="og:image:alt" content={post.title} />
        <meta property="og:locale" content="en_ZA" />
        <meta property="article:published_time" content={post.created_at} />
        <meta property="article:author" content={post.author || 'SA Dropped'} />
        <meta property="article:section" content={post.category} />

        {/* ── Twitter ── */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@sa_dropped" />
        <meta name="twitter:title" content={`${post.title} | SA Dropped Journal`} />
        <meta name="twitter:description" content={post.excerpt || post.title} />
        <meta name="twitter:image" content={postImage} />
        <meta name="twitter:image:alt" content={post.title} />

        {/* ── Schema.org BlogPosting ── */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": ${JSON.stringify(post.title)},
            "description": ${JSON.stringify(post.excerpt || '')},
            "image": ${JSON.stringify(postImage)},
            "url": ${JSON.stringify(postUrl)},
            "datePublished": ${JSON.stringify(post.created_at)},
            "dateModified": ${JSON.stringify(post.created_at)},
            "inLanguage": "en-ZA",
            "author": {
              "@type": "Organization",
              "name": ${JSON.stringify(post.author || 'SA Dropped')}
            },
            "publisher": {
              "@type": "Organization",
              "name": "SA Dropped",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.sadropped.co.za/logo_nav.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": ${JSON.stringify(postUrl)}
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://www.sadropped.co.za"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://www.sadropped.co.za/blog"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": ${JSON.stringify(post.title)},
                  "item": ${JSON.stringify(postUrl)}
                }
              ]
            }
          }
        `}</script>
      </Helmet>

      <article className="bp">
        {/* Cover image */}
        <div className="bp__cover">
          <img
            src={post.cover_image || 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=1400&q=85'}
            alt={post.title}
            className="bp__cover-img"
          />
          <div className="bp__cover-overlay" />
          <div className="bp__cover-content">
            <button className="bp__back" onClick={() => navigate('/blog')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"/>
                <polyline points="12 19 5 12 12 5"/>
              </svg>
              Back to Journal
            </button>
            <span className="bp__category">{post.category}</span>
            <h1 className="bp__title">{post.title}</h1>
            <div className="bp__meta">
              <span>{post.author}</span>
              <span className="bp__meta-sep">·</span>
              <span>{formatDate(post.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="bp__content">
          <div className="bp__lead">{post.excerpt}</div>
          <div className="bp__body">
            {renderBody(post.body)}
          </div>
        </div>

        {/* Related posts */}
        {related.length > 0 && (
          <div className="bp__related">
            <div className="bp__related-inner">
              <p className="bp__related-label">More From The Lab</p>
              <div className="bp__related-grid">
                {related.map(rel => (
                  <div
                    key={rel.id}
                    className="bp__related-card"
                    onClick={() => navigate(`/blog/${rel.slug}`)}
                  >
                    <div className="bp__related-img-wrap">
                      <img
                        src={rel.cover_image || 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&q=80'}
                        alt={rel.title}
                      />
                    </div>
                    <div className="bp__related-body">
                      <span className="bp__related-cat">{rel.category}</span>
                      <h4 className="bp__related-title">{rel.title}</h4>
                      <span className="bp__related-date">{formatDate(rel.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </article>
    </>
  );
}